import { useEffect } from 'react'
import type { MigrationCtx } from '@/lib/migration/useMigration'
import { CHALLENGE_COLUMNS, CHALLENGE_EXAMPLE, PART_A_SECTIONS, exampleText, looksCSuite, partASectionAnswered, metaOf } from '@/lib/migration/engine'
import type { MigrationPlan, OptionSection, PartASectionKey } from '@/api/types/migration'
import { Chip, Icon } from '@/components/readiness/primitives'
import { PlanField, SourceChip } from '@/components/migration/primitives'

// Part A form (I3 §10.2): the six CSA sections as cards in template order;
// checkbox groups rendered as the CSA options with an "Other (specify)"
// input, elaboration textareas, section 6 as a two-column repeating table.
// Every answer is the organisation's own text, saved to qtz-readiness. The
// template's greyed examples are placeholders: they never become a value
// and typing replaces them.

type OptionKey = 's2_budget' | 's4_risk_framework' | 's5_procurement'

export function PartAForm({ ctx, scrollTo, showCsa, onToggleCsa }: { ctx: MigrationCtx; scrollTo?: string; showCsa: boolean; onToggleCsa: () => void }) {
  const { plan: data, actions, summary } = ctx
  useEffect(() => {
    if (scrollTo) document.getElementById(`parta-${scrollTo}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [scrollTo])
  const plan = data.plan
  if (!plan || !summary) return null
  const set = (key: keyof MigrationPlan & string, value: unknown) => actions.updatePlanField(key, value)
  const header = (key: keyof MigrationPlan & string, label: string, placeholder?: string) => (
    <PlanField id={`parta-h-${key}`} label={label} meta={metaOf(plan.field_meta, key)} value={String(plan[key] ?? '')} onChange={(v) => set(key, v)} onBlur={actions.flush} placeholder={placeholder} />
  )

  return (
    <section className="min-w-0 flex-1" aria-label="Part A · Organisation" data-testid="part-a-form">
      <div className="rd-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="flex flex-wrap items-center gap-2"><Chip tone="chip">PART A · ORGANISATION</Chip><Chip tone={summary.part_a.answered === 6 ? 'ok' : 'warn'}>{summary.part_a.answered} / 6 sections</Chip></p>
            <h2 className="mt-1.5 rd-section-title">Organisational level</h2>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Answered once per organisation. Version and submission date are set when you review & export; they are never stored on the live plan.</p>
          </div>
          <label className="flex items-center gap-1.5 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
            <input type="checkbox" checked={showCsa} onChange={onToggleCsa} /> Show CSA wording
          </label>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {header('org_name', 'Organisation name')}
          {header('cii_sector', 'CII sector', 'e.g. Banking & Finance')}
          {header('accountable_exec_name', 'Accountable executive', 'e.g. Jane Tan')}
          <PlanField
            id="parta-h-accountable_exec_title"
            label="Title"
            meta={metaOf(plan.field_meta, 'accountable_exec_title')}
            value={plan.accountable_exec_title ?? ''}
            onChange={(v) => set('accountable_exec_title', v)}
            onBlur={actions.flush}
            placeholder="e.g. Chief Information Security Officer"
            hint={plan.accountable_exec_title.trim() && !looksCSuite(plan.accountable_exec_title) ? <span style={{ color: 'var(--rd-status-warn)' }} data-testid="c-suite-warning">CSA requires the accountable executive to be C-suite or equivalent (e.g. CEO, CIO, CISO, Chief Risk Officer). Keep this title only if it is an equivalent.</span> : undefined}
          />
        </div>
        <div className="mt-3">
          <p className="mb-1 text-[12.5px] font-medium" style={{ color: 'var(--rd-text-secondary)' }}>Point(s) of contact</p>
          <table className="w-full text-[12.5px]">
            <thead><tr style={{ color: 'var(--rd-text-muted)' }}><th className="pb-1 text-left font-medium">Name</th><th className="pb-1 text-left font-medium">Title</th><th className="pb-1 text-left font-medium">Email</th><th /></tr></thead>
            <tbody>
              {plan.contacts.map((c, i) => (
                <tr key={i}>
                  {(['name', 'title', 'email'] as const).map((k) => (
                    <td key={k} className="pr-2 pb-1"><input aria-label={`Contact ${i + 1} ${k}`} className="rd-input !py-1 !text-[12.5px]" value={c[k]} onChange={(e) => set('contacts', plan.contacts.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} /></td>
                  ))}
                  <td className="pb-1"><button type="button" className="rd-link" onClick={() => set('contacts', plan.contacts.filter((_, j) => j !== i))} aria-label={`Remove contact ${i + 1}`}><Icon name="x" size={13} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="rd-link text-[12.5px]" onClick={() => set('contacts', [...plan.contacts, { name: '', title: '', email: '' }])}>+ Add contact</button>
        </div>
      </div>

      {PART_A_SECTIONS.map((s) => {
        const answered = partASectionAnswered(plan, s.key)
        const meta = metaOf(plan.field_meta, s.key)
        // §6: with no rows yet, one empty row shows the template's example;
        // typing into it creates the first row.
        const rows = plan.s6_challenges
        const ghost = rows.length === 0
        const shown = ghost ? [{ blocker: '', support_needed: '' }] : rows
        const updateRow = (i: number, k: 'blocker' | 'support_needed', v: string) => set('s6_challenges', ghost ? [{ blocker: '', support_needed: '', [k]: v }] : rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)))
        return (
          <section key={s.key} id={`parta-${s.key}`} className="rd-card mt-3 p-5" aria-labelledby={`parta-${s.key}-h`} data-testid={`parta-${s.key}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 id={`parta-${s.key}-h`} className="text-[14px] font-bold"><span className="rd-mono" style={{ color: 'var(--rd-text-muted)' }}>{s.n}.</span> {s.title}</h3>
              <span className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: answered ? 'var(--rd-status-ok)' : 'var(--rd-status-warn)' }} aria-label={answered ? 'complete' : 'open'} />
                <SourceChip meta={meta} onRevert={meta.overridden ? () => void actions.revertPlanField(s.key) : undefined} />
              </span>
            </div>
            <p className="mt-1 text-[12.5px] leading-relaxed" style={{ color: showCsa ? 'var(--rd-text-secondary)' : 'var(--rd-text-faint)' }}>{showCsa ? `CSA: “${s.csa}”` : s.csa}</p>
            {s.kind === 'text' && (
              <>
                <textarea aria-label={`${s.title} answer`} className="rd-input mt-3" rows={6} value={String(plan[s.key] ?? '')} placeholder={exampleText(s.example)} onChange={(e) => set(s.key, e.target.value)} onBlur={() => void actions.flush()} />
                {!answered && <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-status-warn)' }}>Not answered · blocking at export.</p>}
              </>
            )}
            {s.kind === 'options' && <OptionsSection sectionKey={s.key as OptionKey} value={plan[s.key as OptionKey]} options={s.options!} example={s.example} onChange={(v) => set(s.key, v)} onBlur={() => void actions.flush()} />}
            {s.kind === 'table' && (
              <div className="mt-3">
                <table className="w-full text-[12.5px]">
                  <thead><tr style={{ color: 'var(--rd-text-muted)' }}><th className="pb-1 text-left font-medium">{CHALLENGE_COLUMNS.blocker}</th><th className="pb-1 text-left font-medium">{CHALLENGE_COLUMNS.support}</th><th /></tr></thead>
                  <tbody>
                    {shown.map((c, i) => (
                      <tr key={i} className="align-top">
                        <td className="pr-2 pb-2"><textarea aria-label={`Blocker ${i + 1}`} className="rd-input !text-[12.5px]" rows={3} value={c.blocker} placeholder={exampleText(CHALLENGE_EXAMPLE.blocker)} onChange={(e) => updateRow(i, 'blocker', e.target.value)} onBlur={() => void actions.flush()} /></td>
                        <td className="pr-2 pb-2"><textarea aria-label={`Support ${i + 1}`} className="rd-input !text-[12.5px]" rows={3} value={c.support_needed} placeholder={exampleText(CHALLENGE_EXAMPLE.support)} onChange={(e) => updateRow(i, 'support_needed', e.target.value)} onBlur={() => void actions.flush()} /></td>
                        <td className="pb-2">{!ghost && <button type="button" className="rd-link" aria-label={`Remove row ${i + 1}`} onClick={() => set('s6_challenges', rows.filter((_, j) => j !== i))}><Icon name="x" size={13} /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button type="button" className="rd-link text-[12.5px]" onClick={() => set('s6_challenges', [...plan.s6_challenges, { blocker: '', support_needed: '' }])}>+ Add row</button>
                {!answered && <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-status-warn)' }}>No challenge recorded · blocking at export.</p>}
              </div>
            )}
          </section>
        )
      })}
      <p className="mt-3 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Autosaved · every change is logged to the plan's history.</p>
    </section>
  )
}

function OptionsSection<O extends string>({ sectionKey, value, options, example, onChange, onBlur }: { sectionKey: PartASectionKey; value: OptionSection<O>; options: Array<{ value: string; label: string }>; example: string; onChange: (v: OptionSection<O>) => void; onBlur: () => void }) {
  const toggle = (o: O) => onChange({ ...value, options: value.options.includes(o) ? value.options.filter((x) => x !== o) : [...value.options, o] })
  return (
    <div className="mt-3">
      <fieldset>
        <legend className="mb-1 text-[12.5px] italic" style={{ color: 'var(--rd-text-muted)' }}>Tick all that apply:</legend>
        <div className="space-y-1.5 text-[14px]">
          {options.map((o) => (
            <label key={o.value} className="flex flex-wrap items-center gap-2">
              <input type="checkbox" checked={value.options.includes(o.value as O)} onChange={() => toggle(o.value as O)} data-testid={`${sectionKey}-${o.value}`} />
              {o.label}
              {o.value === 'other' && value.options.includes('other' as O) && <input aria-label="Other (specify)" className="rd-input !w-auto flex-1 !py-1 !text-[12.5px]" placeholder="Specify" value={value.other_text} onChange={(e) => onChange({ ...value, other_text: e.target.value })} onBlur={onBlur} />}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="mt-3 block text-[12.5px] font-medium" style={{ color: 'var(--rd-text-secondary)' }}>
        Elaboration
        <textarea className="rd-input mt-1" rows={4} value={value.elaboration} placeholder={exampleText(example)} onChange={(e) => onChange({ ...value, elaboration: e.target.value })} onBlur={onBlur} />
      </label>
      {!value.options.length && !value.elaboration.trim() && <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-status-warn)' }}>No option ticked and no elaboration · blocking at export.</p>}
    </div>
  )
}
